import { useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  startCycle,
  setPhase,
  clearCycle,
  selectCyclePhase,
} from "../redux/slices/powerCycleSlice";
import { useRebootDeviceMutation } from "../redux/slices/djangoApi";

const TIMEOUT_MS = 120000;

/**
 * Hook for managing power cycle state for a single device.
 *
 * Phase transitions:
 * - initiated: command sent, waiting for device confirmation
 * - power_off: device confirmed "powering off" or power went OFF
 * - device_offline: device disconnected from EMQX
 * - complete: device back online
 *
 * On page refresh, if device_status is "powering off", the cycling UI
 * resumes automatically since device_status is persisted in the DB.
 *
 * @param {string} serialNumber
 * @param {object} device - current device data
 * @returns {{ phase, isCycling, triggerCycle }}
 */
const usePowerCycle = (serialNumber, device) => {
  const dispatch = useDispatch();
  const phase = useSelector(selectCyclePhase(serialNumber));
  const timerRef = useRef(null);
  const [rebootDevice] = useRebootDeviceMutation();

  const isCycling = !!phase && phase !== "complete";

  // Auto-detect cycling state from persisted device_status on page load
  useEffect(() => {
    if (phase) return; // Already tracking a cycle
    const ds = device?.device_status?.toLowerCase();
    if (ds === "powering off" || ds === "rebooting") {
      dispatch(startCycle(serialNumber));
      dispatch(setPhase({ sn: serialNumber, phase: "power_off" }));
      timerRef.current = setTimeout(() => {
        dispatch(clearCycle(serialNumber));
      }, TIMEOUT_MS);
    }
  }, [device?.device_status, phase, serialNumber, dispatch]);

  // Phase transitions based on device status changes
  useEffect(() => {
    if (!phase || phase === "complete") return;

    const status = device?.status;
    const power = device?.power_state;
    const ds = device?.device_status?.toLowerCase();

    if (phase === "initiated") {
      // Device confirmed the reboot command
      if (ds === "powering off" || ds === "rebooting") {
        dispatch(setPhase({ sn: serialNumber, phase: "power_off" }));
      } else if (status === "OFFLINE") {
        dispatch(setPhase({ sn: serialNumber, phase: "device_offline" }));
      } else if (power === "OFF") {
        dispatch(setPhase({ sn: serialNumber, phase: "power_off" }));
      }
    } else if (phase === "power_off") {
      if (status === "OFFLINE") {
        dispatch(setPhase({ sn: serialNumber, phase: "device_offline" }));
      } else if (ds !== "powering off" && ds !== "rebooting") {
        // Device stayed online (no cell modem) and sent a new status
        // without "powering off" — cycle is done
        dispatch(setPhase({ sn: serialNumber, phase: "complete" }));
        clearTimeout(timerRef.current);
      }
    } else if (phase === "device_offline" && status === "ONLINE") {
      dispatch(setPhase({ sn: serialNumber, phase: "complete" }));
      clearTimeout(timerRef.current);
    }
  }, [
    device?.status,
    device?.power_state,
    device?.device_status,
    phase,
    serialNumber,
    dispatch,
  ]);

  // Auto-clear "complete" phase after 5 seconds
  useEffect(() => {
    if (phase === "complete") {
      const t = setTimeout(() => dispatch(clearCycle(serialNumber)), 5000);
      return () => clearTimeout(t);
    }
  }, [phase, serialNumber, dispatch]);

  // Cleanup timeout on unmount (but don't clear the Redux state)
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const triggerCycle = useCallback(
    async (duration = 10) => {
      await rebootDevice({
        sn: serialNumber,
        data: { duration },
      }).unwrap();
      dispatch(startCycle(serialNumber));
      timerRef.current = setTimeout(() => {
        dispatch(clearCycle(serialNumber));
      }, TIMEOUT_MS);
    },
    [serialNumber, rebootDevice, dispatch]
  );

  return { phase, isCycling, triggerCycle };
};

export default usePowerCycle;
